/**
 * Showcase view for a single creature preview on a glowing pedestal (codex + ending).
 * The model sits at a configurable screen position (view offset) so DOM panels can
 * share the screen. Swapping models animates (dissolve-in pop).
 */
import {
  AmbientLight,
  Color,
  DirectionalLight,
  FogExp2,
  Group,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  PointLight,
  RingGeometry,
  Scene,
  CylinderGeometry,
  MeshStandardMaterial,
  MeshLambertMaterial,
  Vector3,
  AdditiveBlending,
  DoubleSide,
  type Material,
} from 'three';
import { TREE, type StageId } from '@core/tree';
import { disposeTree, loadPreview, makeMotes, makePortal, uni, type PreviewDeps } from './common';

export class PedestalScene {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(35, 1, 0.1, 100);
  private stand = new Group();
  private holder = new Group();
  private ring: Mesh<RingGeometry, MeshBasicMaterial>;
  private portal: ReturnType<typeof makePortal>;
  private motes: ReturnType<typeof makeMotes>;
  private rim: PointLight;
  private current?: Object3D;
  private currentId?: StageId;
  private cache = new Map<StageId, Object3D>();
  private pop = 1;
  private t = 0;
  private token = 0;
  private silhouetteMat = new MeshLambertMaterial({ color: 0x1c3328, emissive: 0x06110b });
  /** Screen-space x of the model centre (0..1). */
  screenX = 0.5;
  screenY = 0.5;
  spin = 0.5;
  /** Extra drag rotation from the user. */
  dragYaw = 0;

  constructor(private readonly deps: PreviewDeps) {
    this.scene.background = new Color(0x020a07);
    this.scene.fog = new FogExp2(0x020a07, 0.06);
    this.scene.add(new AmbientLight(0xbfffe0, 0.9));
    const key = new DirectionalLight(0xffffff, 2.2);
    key.position.set(2, 4, 3);
    this.scene.add(key);
    this.rim = new PointLight(0x97ff5a, 12, 10, 1.5);
    this.rim.position.set(-1.5, 1, -1.5);
    this.scene.add(this.rim);

    const base = new Mesh(new CylinderGeometry(0.9, 1.05, 0.22, 48), new MeshStandardMaterial({ color: 0x0c1f16, roughness: 0.4, metalness: 0.6 }));
    base.position.y = -0.11;
    this.ring = new Mesh(
      new RingGeometry(0.88, 0.98, 64),
      new MeshBasicMaterial({ color: 0x97ff5a, transparent: true, opacity: 0.9, blending: AdditiveBlending, side: DoubleSide, depthWrite: false }),
    );
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.005;
    const glow = new Mesh(
      new RingGeometry(0.2, 1.6, 64),
      new MeshBasicMaterial({ color: 0x97ff5a, transparent: true, opacity: 0.045, blending: AdditiveBlending, depthWrite: false }),
    );
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = -0.2;
    this.stand.add(base, this.ring, glow);
    this.stand.position.y = -0.62;
    this.holder.position.y = 0.05;
    this.scene.add(this.stand, this.holder);

    this.portal = makePortal(2.4);
    this.portal.position.set(0, 0.3, -3.5);
    uni(this.portal).uIntensity.value = 0.45;
    this.scene.add(this.portal);
    this.motes = makeMotes(deps.quality.tier === 'low' ? 60 : 160, new Vector3(8, 10, 6), 0xa8ff7a, 0.35);
    this.scene.add(this.motes);
    this.camera.position.set(0, 0.5, 7.2);
    this.camera.lookAt(0, 0, 0);
  }

  /** Show a stage's preview (or its silhouette). Resolves once swapped. */
  async show(id: StageId, opts: { silhouette?: boolean } = {}): Promise<void> {
    const my = ++this.token;
    const colour = new Color(TREE[id].color);
    this.ring.material.color.copy(colour);
    this.rim.color.copy(colour);
    uni(this.portal).uA.value.copy(colour);
    let obj = this.cache.get(id);
    if (!obj) {
      obj = await loadPreview(id, this.deps, 1.15);
      if (my !== this.token) {
        this.cache.set(id, obj);
        return;
      }
      this.cache.set(id, obj);
    }
    if (this.current) this.holder.remove(this.current);
    this.applySilhouette(obj, !!opts.silhouette);
    this.current = obj;
    this.currentId = id;
    this.holder.add(obj);
    this.pop = 0;
  }

  clear(): void {
    if (this.current) this.holder.remove(this.current);
    this.current = undefined;
    this.currentId = undefined;
  }

  get shownId(): StageId | undefined {
    return this.currentId;
  }

  private applySilhouette(root: Object3D, on: boolean): void {
    root.traverse((o) => {
      const m = o as Mesh;
      if (!m.isMesh) return;
      const ud = m.userData as { uiOrigMat?: Material | Material[] };
      if (on) {
        if (!ud.uiOrigMat) ud.uiOrigMat = m.material;
        m.material = this.silhouetteMat;
      } else if (ud.uiOrigMat) {
        m.material = ud.uiOrigMat;
        delete ud.uiOrigMat;
      }
    });
  }

  update(dt: number): void {
    this.t += dt;
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Shift the projection so the origin lands at (screenX, screenY).
    this.camera.setViewOffset(w, h, (0.5 - this.screenX) * w, (0.5 - this.screenY) * h, w, h);
    this.pop = Math.min(1, this.pop + dt * 2.2);
    const e = 1 - Math.pow(1 - this.pop, 3);
    const back = 1 + Math.sin(e * Math.PI) * 0.12;
    this.holder.scale.setScalar(Math.max(0.001, e * back));
    this.holder.rotation.y = this.t * this.spin + this.dragYaw + (1 - e) * 2.5;
    this.holder.position.y = 0.05 + Math.sin(this.t * 1.4) * 0.04;
    this.ring.material.opacity = 0.6 + Math.sin(this.t * 3) * 0.3;
    uni(this.portal).uTime.value = this.t;
    uni(this.motes).uTime.value = this.t;
  }

  dispose(): void {
    for (const o of this.cache.values()) {
      this.applySilhouette(o, false);
      disposeTree(o);
    }
    this.cache.clear();
    this.clear();
    disposeTree(this.scene);
    this.silhouetteMat.dispose();
  }
}
